import { FORM_FIELD_TYPES, OPTION_FIELD_TYPES } from '../constants/formStudioFieldTypes';
import { isStatusField, isSubStatusField } from './formStudioFormUtils';

export const UI_FIELD_TYPE_STATUS = 'STATUS';
export const UI_FIELD_TYPE_SUB_STATUS = 'SUB_STATUS';

const BASE_FIELD_TYPES = FORM_FIELD_TYPES.filter(
  (type) => ![UI_FIELD_TYPE_STATUS, UI_FIELD_TYPE_SUB_STATUS].includes(type.value)
);

/** Field types shown in the drawer / toolbar (includes virtual workflow types). */
export const UI_FIELD_TYPES = Object.freeze([
  ...BASE_FIELD_TYPES.slice(0, 7),
  { value: UI_FIELD_TYPE_STATUS, label: 'Status' },
  { value: UI_FIELD_TYPE_SUB_STATUS, label: 'Sub-status' },
  ...BASE_FIELD_TYPES.slice(7),
]);

export function toUiFieldType(field) {
  if (!field) return 'STRING';
  if (isStatusField(field)) return UI_FIELD_TYPE_STATUS;
  if (isSubStatusField(field)) return UI_FIELD_TYPE_SUB_STATUS;
  return field.key_type || 'STRING';
}

export function getUiFieldTypeLabel(field) {
  if (isSubStatusField(field)) return 'Sub-status';
  const uiType = toUiFieldType(field);
  const match = UI_FIELD_TYPES.find((type) => type.value === uiType);
  return match?.label || uiType;
}

export function getFieldRoleLabel(field) {
  if (isStatusField(field)) return 'Status';
  if (isSubStatusField(field)) return 'Sub-status';
  if (OPTION_FIELD_TYPES.includes(field?.key_type)) return 'Options';
  return 'Default';
}

export function isUiStatusField(field) {
  return toUiFieldType(field) === UI_FIELD_TYPE_STATUS;
}

export function isUiOptionField(field) {
  const uiType = toUiFieldType(field);
  return OPTION_FIELD_TYPES.includes(uiType) || isUiStatusField(field) || isSubStatusField(field);
}

/**
 * Maps UI/editor field shape to backend contract before save.
 * @param {object} field
 */
export function normalizeFieldForBackend(field) {
  if (!field || typeof field !== 'object') return field;

  const uiType = field.ui_key_type ?? toUiFieldType(field);
  const next = { ...field };
  delete next.ui_key_type;

  if (uiType === UI_FIELD_TYPE_STATUS) {
    return {
      ...next,
      key_type: 'DROPDOWN',
      statusOption: 'is_status',
      status_parent_key: '',
    };
  }

  if (uiType === UI_FIELD_TYPE_SUB_STATUS) {
    return {
      ...next,
      key_type: 'DROPDOWN',
      statusOption: 'is_sub_status',
      status_parent_key: next.status_parent_key || '',
    };
  }

  if (OPTION_FIELD_TYPES.includes(uiType)) {
    return {
      ...next,
      key_type: uiType,
      statusOption: isSubStatusField(next) ? 'is_sub_status' : '',
      status_parent_key: isSubStatusField(next) ? (next.status_parent_key || '') : '',
    };
  }

  return {
    ...next,
    key_type: uiType,
    statusOption: '',
    status_parent_key: '',
  };
}

/**
 * Maps backend field to UI/editor shape after load.
 * @param {object} field
 */
export function normalizeFieldForUi(field) {
  if (!field || typeof field !== 'object') return field;
  return {
    ...field,
    ui_key_type: toUiFieldType(field),
  };
}

export function fieldMatchesTypeFilter(field, typeFilter) {
  if (!typeFilter) return true;
  if (typeFilter === UI_FIELD_TYPE_STATUS) return isStatusField(field);
  if (typeFilter === UI_FIELD_TYPE_SUB_STATUS) return isSubStatusField(field);
  if (typeFilter === 'DROPDOWN') {
    return field?.key_type === 'DROPDOWN' && !isStatusField(field) && !isSubStatusField(field);
  }
  return field?.key_type === typeFilter;
}
